import { SelectOptions } from 'components/commonType';
import { FireableUpdateEventListener, UpdateEventListener } from 'utils/UpdateEventListener';

export enum ItemType {
  auto,
  withCategory,
  autoByGroupSetting
}

export type ItemSetting = {
  type: ItemType;
  categorySelectable: boolean;
  i18nPrefix?: string;
  readonly: boolean;
  hideExtra?: boolean;
};

export function defaultItemSetting () {
  return {
    type: ItemType.auto,
    categorySelectable: true,
    readonly: false
  };
}

export interface SelectItemComponentModel {
  readonly event: UpdateEventListener<SelectItemComponentModel>;
  readonly state: SelectItemComponentState;
  readonly data: SelectOptions;
  readonly singleSelection: boolean;
  readonly allSelectedOptions?: SelectOptions[];
  readonly disabled?: boolean;
  readonly itemSetting: ItemSetting;
  readonly itemHeight: number;
  readonly parentValues: (string | number)[];
  readonly onSelect: (option: SelectOptions) => void;
  readonly isSelected: (option: SelectOptions) => boolean;
  readonly setItemHeight: (height: number) => void;
  readonly toggleOpen: () => void;
  readonly setOpen: (setOpen: boolean) => void;
  readonly updateProps: (
    disabled: boolean,
    allSelectedOptions: SelectOptions[] | undefined,
    parentValues: (string | number)[],
    onSelect: (SelectOptions: SelectOptions) => void,
    getSelectItemModel: (
      itemData: SelectOptions
    ) => DefaultSelectItemComponentModel
  ) => void;
  setData: (itemData) => void;
  getSelectItemModel: (
    itemData: SelectOptions
  ) => DefaultSelectItemComponentModel;
}

export type SelectItemComponentProps = {
  readonly model: SelectItemComponentModel;
  updateParentItemHeight?: () => void;
};

export type SelectItemComponentState = {
  readonly isOpen: boolean;
};

export class DefaultSelectItemComponentModel implements SelectItemComponentModel {
  event: FireableUpdateEventListener<SelectItemComponentModel> =
    new FireableUpdateEventListener<SelectItemComponentModel>();
  data: SelectOptions;
  singleSelection: boolean;
  disabled?: boolean;
  itemSetting: ItemSetting;
  itemHeight: number = 20;
  onSelect: (selectOptions: SelectOptions) => void;
  isOpen: boolean;
  getSelectItemModel: (
    itemData: SelectOptions
  ) => DefaultSelectItemComponentModel;

  constructor (
    data: SelectOptions,
    singleSelection: boolean,
    itemSetting: ItemSetting,
    onSelect: (SelectOptions) => void,
    private listRef: { current: null | any },
    getSelectItemModel: (
      itemData: SelectOptions
    ) => DefaultSelectItemComponentModel,
    disabled?: boolean,
    public allSelectedOptions?: SelectOptions[],
    public parentValues: (string | number)[] = []
  ) {
    this.data = data;
    this.getSelectItemModel = getSelectItemModel;
    this.singleSelection = singleSelection;
    this.itemSetting = itemSetting;
    this.disabled = disabled;
    this.isOpen = false;
    this.onSelect = onSelect;
  }

  get state () {
    return {
      isOpen: this.isOpen
    };
  }

  setData (data) {
    this.data = data;
  }

  updateProps (
    disabled: boolean,
    allSelectedOptions: SelectOptions[] | undefined,
    parentValues: (string | number)[] = [],
    onSelect: (SelectOptions: SelectOptions) => void,
    getSelectItemModel: (
      itemData: SelectOptions
    ) => DefaultSelectItemComponentModel
  ) {
    this.parentValues = parentValues;
    this.disabled = disabled;
    this.allSelectedOptions = allSelectedOptions;
    this.onSelect = onSelect;
    this.getSelectItemModel = getSelectItemModel;
  }

  toggleOpen = () => {
    this.isOpen = !this.isOpen;
    this.updateState();
  }

  setOpen = (isOpen) => {
    this.isOpen = isOpen;
    this.updateState();
  }

  setItemHeight (height: number) {
    if (this.itemHeight === height) {
      return;
    }
    this.itemHeight = height;
    this.listRef.current && this.listRef.current.resetAfterIndex(0);
  }

  checkSelectItemIsSelected (
    option: SelectOptions,
    allSelectedValue: (string | number)[]
  ) {
    if (allSelectedValue.includes(option.value)) {
      return true;
    }
    if (option.options && option.options.length > 0) {
      return option.options.every((subOption: SelectOptions) => {
        return this.checkSelectItemIsSelected(subOption, allSelectedValue);
      });
    }
    return false;
  }

  isSelected (option: SelectOptions) {
    if (!this.allSelectedOptions) {
      return false;
    }

    const selectedValue = this.allSelectedOptions.map((option) => option.value);
    const parentOrOptionSelected = [
      ...this.parentValues,
      this.data.value,
      option.value
    ].some((value) => selectedValue.includes(value));
    if (parentOrOptionSelected) {
      return true;
    }

    if (this.data.value === option.value) {
      return this.checkSelectItemIsSelected(this.data, selectedValue);
    }

    return false;
  }

  updateState () {
    this.event.fireEvent(this);
  }
}
